import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, Link } from 'react-router-dom';
import {
  Home, FolderKanban, Library, Handshake, PenLine, CheckCheck, History, FolderOpen, Sparkles,
  ListChecks, CalendarDays, Users, Settings, Menu, LifeBuoy, Gift, ChartNoAxesColumn, Building2,
  UserRound, LogOut, Lock,
} from 'lucide-react';
import ErrorBoundary from '../components/ErrorBoundary';
import NotificationBell from '../components/NotificationBell';
import WorkspaceSwitcher from '../components/WorkspaceSwitcher';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { Avatar } from '../components/ui';
import { displayName } from '../lib/format';
import Assistant from '../components/Assistant';
import FeedbackWidget from '../components/FeedbackWidget';
import { usePlan } from '../context/PlanContext';
import useSeo from '../lib/seo';
import logo from '../logo';


// Lucide, at a single size and stroke so the whole sidebar reads as one set.
const ico = (Icon) => <Icon size={18} strokeWidth={1.75} aria-hidden="true" />;
const I = {
  home: ico(Home), projects: ico(FolderKanban), bank: ico(Library), partners: ico(Handshake),
  tasks: ico(PenLine), approvals: ico(CheckCheck), past: ico(History), files: ico(FolderOpen),
  ai: ico(Sparkles), compliance: ico(ListChecks), calendar: ico(CalendarDays), team: ico(Users),
  settings: ico(Settings), menu: ico(Menu), support: ico(LifeBuoy), referrals: ico(Gift),
  overview: ico(ChartNoAxesColumn), workspaces: ico(Building2), users: ico(UserRound),
  signOut: ico(LogOut), locked: ico(Lock), org: ico(Building2),
};

export default function AppShell() {
  const { user, isAdmin, isApprover, signOut } = useAuth();
  const { has, plan } = usePlan();
  const writerMode = plan?.kind === 'writer';
  useSeo({ title: user?.company?.name || 'Workspace', path: '/app', noindex: true });
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({});
  const [staff, setStaff] = useState(false);
  useEffect(() => { api.get('/api/staff/access').then(r => setStaff(Boolean(r.data.staff))).catch(() => {}); }, []);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    api.get('/api/projects/dashboard/summary').then(res => { if (!cancelled) setCounts(res.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [location.pathname]);

  const link = (to, label, icon, count, feature) => (
    <NavLink to={to} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} end={to === '/app' || to === '/app/staff' || to === '/app/projects'}>
      <span className="nav-icon">{icon}</span>{label}
      {count > 0 && <span className="nav-count">{count}</span>}
      {feature && !has(feature) && <span className="nav-lock" title="Premium feature">{I.locked}</span>}
    </NavLink>
  );

  return (
    <div className="shell">
      <div className={`sidebar-scrim ${open ? 'open' : ''}`} onClick={() => setOpen(false)} />
      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <Link to="/app"><img src={logo} alt="Merge" /></Link>
        </div>
        <div className="sidebar-workspace">
          <WorkspaceSwitcher current={user?.company?.name} canCreate={Boolean(plan?.features?.includes('multi_workspace'))} />
          <div className="role">{user?.role}{plan ? (plan.staff ? ' · Merge staff' : ` · ${plan.name}${plan.trialing ? ' trial' : plan.pilot ? ' pilot' : plan.comped ? ' · complimentary' : ''}`) : ''}</div>
        </div>
        <nav className="sidebar-nav">
          {link('/app', 'Home', I.home)}
          {link('/app/projects', 'Projects', I.projects)}
          {!writerMode && link('/app/tasks', 'My tasks', I.tasks, counts.myOpenQuestions)}
          {!writerMode && link('/app/approvals', 'Approvals', I.approvals, isApprover ? counts.awaitingMyApproval : counts.pendingApproval)}
          <div className="nav-section">Tools</div>
          {link('/app/organization', 'Organization', I.org)}
          {link('/app/answer-bank', 'Answer bank', I.bank)}
          {link('/app/ai-review', 'AI reviewer', I.ai, 0, 'ai_reviewer')}
          {link('/app/compliance', 'Compliance check', I.compliance)}
          {link('/app/past-proposals', 'Past proposals', I.past, 0, 'past_proposals')}
          {link('/app/partners', 'Partners', I.partners, 0, 'partners')}
          {link('/app/files', 'File cabinet', I.files)}
          {link('/app/calendar', 'Grant calendar', I.calendar)}
          <div className="nav-section">Workspace</div>
          {isAdmin && (!writerMode || plan?.staff) && link('/app/team', 'Team', I.team)}
          {!plan?.staff && link('/app/referrals', 'Refer a friend', I.referrals)}
          {link('/app/support', 'Support', I.support)}
          {link('/app/settings', 'Settings', I.settings)}
          {staff && <div className="nav-section">Merge staff</div>}
          {staff && link('/app/staff/overview', 'Overview', I.overview)}
          {staff && link('/app/staff', 'Workspaces & pilots', I.workspaces)}
          {staff && link('/app/staff/users', 'Users', I.users)}
          {staff && link('/app/staff/tickets', 'Tickets', I.support)}
        </nav>
        <div className="sidebar-footer">
          <Link to="/app/settings" className="user-chip">
            <Avatar user={user} />
            <div className="who">
              <div className="n truncate">{displayName(user)}</div>
              <div className="e truncate">{user?.email}</div>
            </div>
          </Link>
          <button className="nav-link" style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer' }} onClick={signOut}>
            <span className="nav-icon">{I.signOut}</span>Sign out
          </button>
          <div className="powered-side">Powered by <a href="https://badjrtech.com" target="_blank" rel="noopener noreferrer">Badjr</a></div>
        </div>
      </aside>
      <div className="main">
        <header className="topbar">
          <button className="btn btn-secondary btn-icon menu-btn" onClick={() => setOpen(o => !o)} aria-label="Menu">{I.menu}</button>
          <span className="crumbs">{user?.company?.name}</span>
          <NotificationBell />
          {plan?.trialing && !plan.staff && (
            <Link to="/app/settings#plan" className="trial-pill">{plan.name} trial · {plan.trialDaysLeft} day{plan.trialDaysLeft === 1 ? '' : 's'} left · Choose a plan</Link>
          )}
          {plan?.pilot && (
            <Link to="/app/settings#plan" className="trial-pill">{plan.name} pilot · {plan.pilotDaysLeft} day{plan.pilotDaysLeft === 1 ? '' : 's'} free{plan.pilotRevertsToName && plan.pilotRevertsToName !== plan.name ? ` · then ${plan.pilotRevertsToName}` : ''}</Link>
          )}
          {plan?.trialExpired && plan.key === 'free' && (
            <Link to="/app/settings#plan" className="trial-pill ended">Trial ended · you're on Free · See plans</Link>
          )}
        </header>
        <main className="content">
          <ErrorBoundary routeKey={location.pathname}><Outlet /></ErrorBoundary>
        </main>
      </div>
      <Assistant />
      <FeedbackWidget />
    </div>
  );
}
